/**
 * Client-side RUM (Real User Monitoring) drain.
 *
 * The first-party "drain" for the same field signals Vercel Analytics / Speed
 * Insights collect into closed dashboards: Core Web Vitals (from
 * `RumReporter`'s `useReportWebVitals`) and a sample of the registry events
 * already flowing through `track()` (the third sink in `gtag.ts`'s `sendEvent`).
 * Samples are buffered in memory and flushed as a single `navigator.sendBeacon`
 * POST to `/api/rum/ingest` on page-hide / tab-hide (or when the buffer fills),
 * so the hot event path only ever does an O(1) array push — no per-event
 * network call, no main-thread or layout cost.
 *
 * Opt-in + privacy posture (matches the GSC loop):
 *   - Off unless `NEXT_PUBLIC_RUM_ENABLED === 'true'`, so nothing is sent (and
 *     no listeners are bound) until the drain is deliberately provisioned.
 *   - Consent-gated via `isRumConsentGranted()`: `isAnalyticsAllowed()` PLUS the
 *     region-aware default-deny (EEA/UK/CH need an explicit opt-in, since this
 *     first-party drain bypasses Google Consent Mode), mirroring attribution.
 *     When the middleware's region cookie is absent (cacheable hop), the flush
 *     gate defers transmission until the shared region-hydration signal
 *     settles — the await-free equivalent of `isAnalyticsStorageAllowedAsync`
 *     (see `rumFlushDecision`).
 *   - Every buffered item is stamped with the CONSENT EPOCH it was captured
 *     under (`~/lib/analytics/rum-consent`), and only same-epoch items may
 *     transmit — so consent withdrawal, an opt-in in a consent-required
 *     region, and a consent-required region resolution each structurally
 *     invalidate everything captured before them (r3543582085).
 *   - Only a whitelisted, bounded subset of event properties is forwarded; no
 *     full GA4 param bag, no query strings (paths are stripped).
 */

import {
  hasExplicitAnalyticsOptIn,
  isAnalyticsAllowed,
} from "~/lib/analytics/consent"
import {
  getHydratedConsentRequiredSignal,
  hydrateRegionCookieIfMissing,
} from "~/lib/analytics/hydrate-region-cookie"
import { currentNavigationReferrerHost } from "~/lib/analytics/navigation-referrer"
import {
  currentRumConsentEpoch,
  hasRegionCookieClient,
  isConsentRequiredRegionClient,
  isRumConsentGranted,
} from "~/lib/analytics/rum-consent"
import {
  FORWARDED_EVENT_PROP_KEYS,
  RUM_VIEW_INSTANCE_KEY,
  type RumEventSample,
  type WebVitalSample,
} from "~/lib/rum/types"

const INGEST_PATH = "/api/rum/ingest"
/** Flush eagerly once either buffer reaches this many samples. */
const FLUSH_AT = 20
/** Hard cap so a long-lived tab can't grow the buffers unbounded. */
const MAX_BUFFER = 100
/** Per-beacon item + byte budgets, both safely under the ingest route's
 *  per-array (30) and body-size (32 KiB) caps so a beacon is never server-rejected. */
const MAX_BEACON_ITEMS = 25
const MAX_BEACON_BYTES = 30 * 1024

/**
 * Buffered items carry the CONSENT EPOCH they were captured under
 * (r3543582085 — see `~/lib/analytics/rum-consent`): the flush transmits only
 * same-epoch items, so samples captured under a consent that was later
 * withdrawn (or in a region later resolved consent-required) can never
 * transmit — even when the invalidation and a re-grant both happen between
 * flushes, the case the flush-time `drop` re-check alone could not see.
 */
interface Stamped<T> {
  epoch: number
  sample: T
}

const vitalsBuffer: Array<Stamped<WebVitalSample>> = []
const eventsBuffer: Array<Stamped<RumEventSample>> = []
let listenersBound = false
/** Per-page-load `resource_view` token sequence (see enqueueRumEvent). */
let viewInstanceSeq = 0
let referrerHostCaptured = false
let navigationReferrerHost: string | undefined

function captureNavigationReferrerHost(): void {
  if (referrerHostCaptured) return
  referrerHostCaptured = true
  navigationReferrerHost = currentNavigationReferrerHost()
}

function isEnabled(): boolean {
  return (
    typeof window !== "undefined" &&
    process.env.NEXT_PUBLIC_RUM_ENABLED === "true"
  )
}

/**
 * Region-signal lifecycle for the AWAIT-FREE transmission gate below. The
 * exposure-beacon / conversion-ledger writes can `await
 * isAnalyticsStorageAllowedAsync()`; the RUM flush cannot — it runs on
 * `pagehide`, where an async continuation may never execute. So hydration is
 * kicked off at the FIRST enqueue (deduped by the shared helper, and usually
 * already done by ConsentBanner's own mount-time hydration), and the flush
 * gate consults the helper's CACHED outcome synchronously.
 */
let regionHydrationSettled = false
let regionHydrationStarted = false

function ensureRegionSignal(): void {
  if (regionHydrationSettled || regionHydrationStarted) return
  if (typeof document === "undefined") return
  if (hasRegionCookieClient()) {
    regionHydrationSettled = true
    return
  }
  regionHydrationStarted = true
  // The helper resolves (never rejects) with UNRESOLVED on failure; the catch
  // is belt-and-suspenders so a surprise rejection can't wedge the drain in
  // "defer" forever.
  //
  // INVARIANT: samples queued while the region was UNKNOWN never transmit
  // once the region resolves consent-required — unconditionally, not just
  // when no opt-in exists at resolution time (r3541077076 + r3541422292).
  // This used to be a buffer clear right here; it is now enforced by the
  // CONSENT EPOCH (`~/lib/analytics/rum-consent`, r3543582085): the resolved
  // consent-required signal is a one-way latch the epoch edge-detects on its
  // next read, so every pre-resolution item (stamped under the old epoch) is
  // purged at flush — including items held in gtag's pre-bind queue, which no
  // clear inside this module could reach (r3543582090).
  void hydrateRegionCookieIfMissing()
    .then(() => {
      regionHydrationSettled = true
    })
    .catch(() => {
      regionHydrationSettled = true
    })
}

type RumFlushDecision = "send" | "drop" | "defer"

/**
 * The TRANSMISSION gate — decides what a flush may do with the buffers. The
 * queueing gate above is memory-only; nothing leaves the page until this says
 * `send`. Same decision table as `isAnalyticsStorageAllowedAsync` (#479),
 * expressed await-free for the pagehide flush path (r3540576136):
 *
 *   - consent denied / withdrawn → `drop` (clear, never transmit)
 *   - explicit opt-in            → `send` (satisfies every region)
 *   - region cookie present      → cookie decides (`drop` in EEA/UK/CH
 *                                  without opt-in, else `send`)
 *   - no cookie, hydration confirmed consent-required → `drop`
 *   - no cookie, hydration still PENDING → `defer` (keep buffers; a later
 *     flush re-decides — a pagehide in this sub-second race loses the beacon,
 *     which is the safe direction)
 *   - no cookie, hydration settled WITHOUT confirming consent-required
 *     (incl. unresolved geo / fetch failure) → `send` — the rest-of-world
 *     fail-open default the shared async gate + attribution layer use; going
 *     stricter here would reduce first-party collection beyond the sitewide
 *     posture (docs/analytics/first-party-analytics-posture.md).
 */
function rumFlushDecision(): RumFlushDecision {
  if (!isAnalyticsAllowed()) return "drop"
  // Region KNOWN via the middleware cookie: the cookie decides, with the
  // explicit opt-in unlocking consent-required regions.
  if (hasRegionCookieClient()) {
    if (isConsentRequiredRegionClient() && !hasExplicitAnalyticsOptIn()) {
      return "drop"
    }
    return "send"
  }
  // No cookie. The hydrated signal (blocked Set-Cookie hop) counts as a KNOWN
  // consent-required region — opt-in unlocks it (anything pre-resolution was
  // already cleared at settle, so the buffers hold post-resolution samples).
  if (getHydratedConsentRequiredSignal()) {
    return hasExplicitAnalyticsOptIn() ? "send" : "drop"
  }
  // Region UNKNOWN (hydration pending): defer EVEN WITH an opt-in
  // (r3543138830). The opt-in short-circuit used to run first, so a
  // pagehide/buffer-full flush landing before hydration settled transmitted
  // unknown-region samples — the settle-time clear (r3541422292) came too
  // late for that flush. Opt-in only decides once the region is known; the
  // airtight invariant is "nothing transmits while the region is unresolved".
  if (!regionHydrationSettled) {
    ensureRegionSignal()
    return "defer"
  }
  // Settled without a consent-required signal (incl. unresolved geo / fetch
  // failure): the sitewide rest-of-world fail-open default.
  return "send"
}

/** Strip query string + fragment so high-cardinality params can't explode the
 *  route dimension; keep just the pathname. */
function cleanPath(input?: string): string {
  const raw = input ?? window.location.pathname
  const noHash = raw.split("#")[0] ?? raw
  const noQuery = noHash.split("?")[0] ?? noHash
  return noQuery || "/"
}

function bodyBytes(body: string): number {
  // `Blob.size` is the UTF-8 byte length and is available in every browser (and
  // jsdom), avoiding a TextEncoder dependency.
  return new Blob([body]).size
}

function beaconBody(
  vitals: WebVitalSample[],
  events: RumEventSample[],
): string {
  return JSON.stringify({
    vitals,
    events,
    ...(navigationReferrerHost
      ? { referrer_host: navigationReferrerHost }
      : {}),
  })
}

/** POST one already-bounded beacon body. Best-effort, fire-and-forget. */
function postBeacon(body: string): void {
  try {
    const blob = new Blob([body], { type: "application/json" })
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(INGEST_PATH, blob)
    ) {
      return
    }
    // Fallback for browsers without sendBeacon: keepalive fetch so it survives
    // an unload. Swallow errors — telemetry must never break UX.
    void fetch(INGEST_PATH, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Never let the drain throw into the caller.
  }
}

/**
 * Flush the buffers to the ingest route in SIZE-BOUNDED beacons. Each beacon is
 * greedily packed up to MAX_BEACON_ITEMS and MAX_BEACON_BYTES, both safely under
 * the server's per-array (30) and body-size (32 KiB) caps — otherwise a large
 * batch would 400/413 and the spliced samples would be silently dropped (review
 * feedback). Best-effort, fire-and-forget.
 */
export function flushRumDrain(): void {
  if (!isEnabled()) return
  // Consent can be WITHDRAWN after samples were queued, and the region signal
  // can be UNRESOLVED (cacheable hop — no middleware cookie yet). Re-decide at
  // flush time: `drop` clears without transmitting (otherwise a page-hide
  // flush would send first-party telemetry collected before the opt-out —
  // review feedback), `defer` keeps the buffers untouched until the region
  // signal settles (r3540576136).
  const decision = rumFlushDecision()
  if (decision === "defer") return
  if (decision === "drop") {
    vitalsBuffer.length = 0
    eventsBuffer.length = 0
    return
  }
  // `send` — but ONLY same-epoch items may transmit (r3543582085): a
  // withdraw→re-grant cycle (or a consent-required region resolution) between
  // flushes leaves this decision `send` while the buffered items were captured
  // under a since-invalidated consent. Purge them before packing.
  purgeStaleEpochs(vitalsBuffer)
  purgeStaleEpochs(eventsBuffer)
  while (vitalsBuffer.length > 0 || eventsBuffer.length > 0) {
    const vitals: WebVitalSample[] = []
    const events: RumEventSample[] = []
    while (vitals.length + events.length < MAX_BEACON_ITEMS) {
      const nextEvent = eventsBuffer[0]
      if (nextEvent !== undefined) {
        events.push(nextEvent.sample)
        if (bodyBytes(beaconBody(vitals, events)) > MAX_BEACON_BYTES) {
          events.pop()
          // A single item over the budget (shouldn't happen given prop caps) —
          // drop it rather than loop forever.
          if (vitals.length + events.length === 0) eventsBuffer.shift()
          break
        }
        eventsBuffer.shift()
        continue
      }
      const nextVital = vitalsBuffer[0]
      if (nextVital !== undefined) {
        vitals.push(nextVital.sample)
        if (bodyBytes(beaconBody(vitals, events)) > MAX_BEACON_BYTES) {
          vitals.pop()
          if (vitals.length + events.length === 0) vitalsBuffer.shift()
          break
        }
        vitalsBuffer.shift()
        continue
      }
      break // both buffers empty
    }
    if (vitals.length + events.length === 0) continue
    postBeacon(beaconBody(vitals, events))
  }
}

/** Drop every buffered item whose capture epoch is no longer current. */
function purgeStaleEpochs<T>(buffer: Array<Stamped<T>>): void {
  const epoch = currentRumConsentEpoch()
  let write = 0
  for (const entry of buffer) {
    if (entry.epoch === epoch) {
      buffer[write] = entry
      write += 1
    }
  }
  buffer.length = write
}

function bindListeners(): void {
  if (listenersBound || typeof window === "undefined") return
  listenersBound = true
  // `visibilitychange → hidden` and `pagehide` are the reliable end-of-session
  // signals on both desktop and mobile (mobile rarely fires `unload`).
  const onHide = (): void => {
    if (document.visibilityState === "hidden") flushRumDrain()
  }
  document.addEventListener("visibilitychange", onHide)
  window.addEventListener("pagehide", flushRumDrain)
}

function afterEnqueue(totalQueued: number): void {
  bindListeners()
  if (totalQueued >= FLUSH_AT) flushRumDrain()
}

/** Buffer one Web Vitals sample (called by `RumReporter`). */
export function enqueueRumVital(sample: WebVitalSample): void {
  if (!isEnabled()) return
  // Kick region hydration BEFORE the gate so an unhydrated-cookie session
  // starts resolving immediately — the flush gate defers transmission until
  // it settles (r3540576136).
  ensureRegionSignal()
  if (!isRumConsentGranted()) return
  captureNavigationReferrerHost()
  if (vitalsBuffer.length >= MAX_BUFFER) vitalsBuffer.shift()
  vitalsBuffer.push({
    epoch: currentRumConsentEpoch(),
    sample: { ...sample, path: cleanPath(sample.path) },
  })
  afterEnqueue(vitalsBuffer.length)
}

/**
 * Buffer one first-party event (the third sink in `sendEvent`). Forwards only
 * the event name, the current path, and a whitelisted subset of properties.
 * The caller (`sendEvent`) has already passed the consent gate, but we re-check
 * defensively so this is safe to call from anywhere.
 */
export function enqueueRumEvent(
  name: string,
  eventParams: Record<string, unknown>,
): void {
  if (!isEnabled()) return
  ensureRegionSignal()
  if (!isRumConsentGranted()) return
  captureNavigationReferrerHost()
  const props: Record<string, unknown> = {}
  for (const key of FORWARDED_EVENT_PROP_KEYS) {
    const v = eventParams[key]
    if (v === undefined || v === null) continue
    // Keep only primitives, and bound string length.
    if (typeof v === "string") props[key] = v.slice(0, 256)
    else if (typeof v === "number" || typeof v === "boolean") props[key] = v
  }
  // Stamp a per-enqueue view token (r3547249797): `ResourceAnalytics`
  // single-fires per mount, so one enqueue = one legitimate view instance.
  // The ingest route dedupes `resource_view` on (path, token) — distinct
  // mounts of the same route inside one flush window (SPA back-and-forth)
  // all count, while identical stacked copies still collapse — and strips
  // the token before persisting. Stamped AFTER the whitelist loop so a
  // caller-supplied value can't ride through. A simple per-load sequence is
  // sufficient: uniqueness only matters within one beacon (one request).
  if (name === "resource_view") {
    viewInstanceSeq += 1
    props[RUM_VIEW_INSTANCE_KEY] = `v${viewInstanceSeq}`
  }
  const pathParam = eventParams.page_path
  if (eventsBuffer.length >= MAX_BUFFER) eventsBuffer.shift()
  eventsBuffer.push({
    epoch: currentRumConsentEpoch(),
    sample: {
      name,
      path: cleanPath(typeof pathParam === "string" ? pathParam : undefined),
      props,
    },
  })
  afterEnqueue(eventsBuffer.length)
}
